import { createSelector } from '@reduxjs/toolkit';
import { RootInitialState } from 'core/common/store/rootReducer';
import { getBaseConditionAttributes } from 'core/funnel/store/baseConditionAttributes';
import { Currency, CurrencySymbol } from 'core/payments/entities';
import { sortOffers } from '../entities';
import { resolveActiveOffers } from '../utils/resolveActiveOffers';

export const getOffersLoadingStatus = (state: RootInitialState) => {
  return state.offers.loadingStatus;
};

export const getOffersEntries = (state: RootInitialState) => {
  return state.offers.entries;
};

export const getActiveOffers = createSelector(
  [getOffersEntries, getBaseConditionAttributes],
  resolveActiveOffers,
);

export const getSelectedTrialOffer = (state: RootInitialState) => {
  return state.offers.selectedTrialOffer;
};

export const getSelectedOffer = (state: RootInitialState) => {
  return state.offers.selectedOffer;
};

export const getSelectedUpsell = (state: RootInitialState) => {
  return state.offers.selectedUpsell;
};

export const getDefaultOffers = createSelector(
  [(state: RootInitialState) => getActiveOffers(state).defaultOffers],
  (offers) => sortOffers(offers),
);

// TODO take currency from /user/location endpoint
export const getUserCurrency = createSelector([getDefaultOffers], (offers) => {
  const offer = offers[0];

  if (!offer) {
    return {
      currency: Currency.USD,
      currencySymbol: CurrencySymbol.USD,
    };
  }

  return {
    currency: offer.currency,
    currencySymbol: offer.currencySymbol,
  };
});

export const getTimeUpOffers = createSelector(
  [(state: RootInitialState) => getActiveOffers(state).timeUpOffers],
  (offers) => sortOffers(offers),
);

export const getDiscountOffers = createSelector(
  [(state: RootInitialState) => getActiveOffers(state).discountOffers],
  (offers) => sortOffers(offers),
);

export const getUpsells = createSelector([getActiveOffers], (offers) => offers.upsells);

export const getDiscountUpsells = createSelector(
  [getActiveOffers],
  (offers) => offers.discountUpsells,
);

export const getTimeUpOffer = (state: RootInitialState) => {
  const selectedOffer = getSelectedOffer(state);
  const timeUpOffers = getTimeUpOffers(state);

  if (!timeUpOffers.length) {
    return null;
  }

  const filteredOffer = timeUpOffers.filter(
    (item) => !!selectedOffer && item.trialPrice === selectedOffer.trialPrice,
  );

  return filteredOffer[0] || timeUpOffers[0];
};

export const getTimeUpOfferSamePeriod = createSelector(
  [getSelectedOffer, getTimeUpOffers],
  (selectedOffer, timeUpOffers) =>
    timeUpOffers.find((offer) => offer.trialPeriod === selectedOffer?.trialPeriod) ?? null,
);

export const getDiscountOfferNextPeriod = createSelector(
  [getSelectedOffer, getDefaultOffers, getDiscountOffers],
  (selectedOffer, defaultOffers, discountOffers) => {
    const selectedOfferIndex = defaultOffers.findIndex(
      ({ trialPeriod }) => trialPeriod === selectedOffer?.trialPeriod,
    );

    if (selectedOfferIndex === -1) return null;

    const nextOffer = defaultOffers[selectedOfferIndex + 1];

    if (!nextOffer) return null;

    return discountOffers.find(({ trialPeriod }) => trialPeriod === nextOffer.trialPeriod) ?? null;
  },
);

export const getTrialTimeUpOffer = (state: RootInitialState) => {
  const selectedTrialOffer = getSelectedTrialOffer(state);
  const timeUpOffers = getTimeUpOffers(state);

  if (!timeUpOffers.length) {
    return null;
  }

  const filteredOffer = timeUpOffers.filter(
    (item) => !!selectedTrialOffer && item.trialPrice === selectedTrialOffer.trialPrice,
  );

  return filteredOffer[0] || timeUpOffers[0];
};

export const getMostExpensiveTimeUpOffer = (state: RootInitialState) => {
  const timeUpOffers = getTimeUpOffers(state);

  if (!timeUpOffers.length) {
    return null;
  }

  return timeUpOffers[timeUpOffers.length - 1];
};

export const getDiscountOffer = createSelector(
  [getDiscountOffers],
  (discountOffers) => discountOffers[0] ?? null,
);
